import { Component, inject, OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';

import { InvoiceService } from '@app/shared/data-invoice';
import { RequestService } from '@pisci/requestManager';
import * as _ from 'lodash';
import { isArray, isObject, isString } from 'lodash';
import {
  catchError,
  combineLatest,
  debounceTime,
  map,
  mergeMap,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import { IOrder } from '@app/data-interfaces';
import {
  CellClickedEvent,
  ColDef,
  GridApi,
  GridReadyEvent,
} from 'ag-grid-community';
import { format, parseISO } from 'date-fns';
import { DataTableActionButtonComponent } from '@cw/ui-core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-invoice',
  templateUrl: './invoices.component.html',
  styleUrls: ['./invoices.component.scss'],
})
export class InvoicesComponent {
  searchControl = new UntypedFormControl('');
  orders$: Observable<IOrder[]>;
  router = inject(Router);
  public pageNumber = 0;
  limit = 50;
  orderFormGroup = new UntypedFormGroup({
    orderIds: new UntypedFormArray([]),
  });
  ordersData: any[] = [];
  showSkipNumberModal = false;
  skippedInvoiceNumber = '';
  skippingNumber = false;
  creatingExternalInvoice = false;

  public columnDefs: ColDef[] = [
    {
      field: 'id',
    },
    {
      field: 'number',
    },
    { field: 'billing_address.name' },

    {
      field: 'billing_address.city',
    },
    {
      field: 'created_at',
      valueFormatter: (params) => {
        return format(parseISO(params.value), 'dd.MM.yyyy');
      },
    },
    { field: 'total' },

    {
      field: 'action',
      cellRenderer: DataTableActionButtonComponent,
      cellRendererParams: {
        buttons: [
          {
            label: 'Detail',
            action: (data: any) => {
              this.router.navigateByUrl('/invoices/' + data.id);
            },
          },
        ],
      },
    },
  ];
  // DefaultColDef sets props common to all Columns
  public defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
  };
  // Data that gets displayed in the grid
  private gridApi!: GridApi;

  constructor(
    private orderService: InvoiceService,
    private rm: RequestService,
    private snackBar: MatSnackBar,
  ) {
    this.orders$ = combineLatest([
      this.searchControl.valueChanges.pipe(debounceTime(1000), startWith('')),
    ]).pipe(
      switchMap(([search]) => {
        if (search == '') {
          return this.orderService.getAllInvoices();
        } else {
          return this.orderService.searchInvoices(search);
        }
      }),
      takeUntilDestroyed(),
    );
  }

  getRowId(params: any) {
    return params.data.id;
  }

  onCellClicked(e: CellClickedEvent): void {
    // this.router.navigateByUrl('invoice/'+e.data.id);
  }

  clickInvoices() {
    const ids = this.gridApi.getSelectedRows().map((row) => row.id);
    return this.rm
      .postGetFile('/invoices/pdf', {
        ids,
      })
      .pipe(
        catchError((err) => {
          console.error(err);
          throw err;
        }),
      )
      .subscribe((response: any) => {
        const blob = new Blob([response.body], {
          type: response.headers.get('content-type'),
        });
        //  let fileName =  response.headers.get('content-disposition').split(';')[0];
        const fileName = 'report.pdf';
        const file = new File([blob], fileName, {
          type: response.headers.get('content-type'),
        });
        const url = window.URL.createObjectURL(blob);
        window.open(url);
      });
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.orders$
      .pipe(debounceTime(1))
      .subscribe((orders) => this.gridApi.setGridOption('rowData', orders));
  }

  clickSkipInvoiceNumber() {
    if (this.skippingNumber) {
      return;
    }

    this.skippingNumber = true;
    this.orderService
      .skipNextInvoiceNumber()
      .pipe(
        finalize(() => {
          this.skippingNumber = false;
        }),
      )
      .subscribe({
        next: (response) => {
          this.skippedInvoiceNumber = response?.skipped_number ?? '';
          this.showSkipNumberModal = true;
        },
        error: () => {
          this.snackBar.open('Skipping invoice number failed.');
        },
      });
  }

  closeSkipNumberModal() {
    this.showSkipNumberModal = false;
  }

  clickCreateExternalInvoice() {
    if (this.creatingExternalInvoice) {
      return;
    }

    const selectedRows = this.gridApi?.getSelectedRows() ?? [];
    if (selectedRows.length !== 1) {
      this.snackBar.open('Please select exactly one invoice.');
      return;
    }

    const selectedInvoiceId = selectedRows[0]?.id;
    if (!selectedInvoiceId) {
      this.snackBar.open('Selected invoice has no id.');
      return;
    }

    this.creatingExternalInvoice = true;
    this.orderService
      .get(selectedInvoiceId)
      .pipe(
        mergeMap((invoice: any) => {
          const body = {
            lines: (invoice?.line_items ?? []).map((line: any) => ({
              name: line.name,
              quantity: line.quantity,
              amountGross: line.amount_gross ?? line.amountGross,
              amountNet: line.amount_net ?? line.amountNet,
              productId: line.product_id ?? line.productId,
            })),
            billingAddress: {
              firstname: invoice?.billing_address?.firstname,
              lastname: invoice?.billing_address?.lastname,
              name: invoice?.billing_address?.name,
              street: invoice?.billing_address?.street,
              street_number: invoice?.billing_address?.street_number,
              zip: invoice?.billing_address?.zip,
              city: invoice?.billing_address?.city,
              country: invoice?.billing_address?.country,
            },
            shippingAddress: {
              firstname: invoice?.shipping_address?.firstname,
              lastname: invoice?.shipping_address?.lastname,
              name: invoice?.shipping_address?.name,
              street: invoice?.shipping_address?.street,
              street_number: invoice?.shipping_address?.street_number,
              zip: invoice?.shipping_address?.zip,
              city: invoice?.shipping_address?.city,
              country: invoice?.shipping_address?.country,
            },
            payment_method: invoice?.payment_method ?? 'invoice',
            data: {
              external: true,
              reference: `EXT-${invoice?.number ?? invoice?.id}`,
            },
          };

          return this.orderService.createExternalInvoice(body);
        }),
        finalize(() => {
          this.creatingExternalInvoice = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          const invoiceId = response?.id ?? response?.data?.id;
          this.snackBar.open('External invoice created.');
          if (invoiceId) {
            this.router.navigateByUrl('/invoices/' + invoiceId);
          }
        },
        error: () => {
          this.snackBar.open('Creating external invoice failed.');
        },
      });
  }
}
